/**
 * Custom Form Widget - Embeddable Bundle Entry Point
 *
 * An MP custom form (Forms / Form_Fields) on woodsidebible.org, replacing the
 * stock <mpp-custom-form> on the ministry pages, the campus pages and /forms/.
 *
 * Embed:
 *   <div id="custom-form-root" data-form="<Form_GUID or Form_ID>"></div>
 *   <script src="https://widgets.woodsidebible.org/custom-form/widget.js"></script>
 *
 * Without `data-form` the form comes from the page's `?formguid=` (what the
 * stock /forms/ page read), `?form=` or `?id=`. The form's own MP title shows
 * by default, so a page needs no heading of its own; `data-title="0"` hides
 * it where the page already carries one, and any other string replaces it
 * where the form's internal name is not what a visitor should read
 * ("Connect Card" on a campus page). The form's instructions always show.
 *
 * What the page can answer for somebody, so they do not have to:
 *   data-campus="Lake Orion"     the campus question, on a campus page
 *   data-interest="Baptism"      ticks that box in a multiselect group
 *   data-answers='{"17488":"Prayer Request"}'   by Form_Field_ID, exact
 * Presets are shown on screen and stay editable; nothing is hidden.
 */

import React from 'react';
import { CustomForm } from '../src/components/CustomForm';
import { attr } from './params';
import { ShadowWidget } from './shell';

interface WidgetConfig {
  apiBaseUrl?: string;
  containerId?: string;
  form?: string;
}

declare global {
  interface Window {
    CUSTOM_FORM_WIDGET_CONFIG?: WidgetConfig;
    CustomFormWidget?: ShadowWidget;
    /**
     * Mount a container that did not exist when the bundle booted. The campus
     * hero creates its Connect Card container the first time its message
     * sheet opens, which may be after this script has already run.
     */
    CustomFormMount?: (container: HTMLElement) => ShadowWidget;
  }
}

const DEFAULT_CONTAINER_ID = 'custom-form-root';
const LEGACY_CONTAINER_ID = 'customForm';

function query(): URLSearchParams {
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams();
  }
}

function formFromPage(container: HTMLElement, config: WidgetConfig): { ref: string | null } {
  const fromAttr =
    config.form ?? attr(container.dataset.form) ?? attr(container.dataset.formguid);
  if (fromAttr) return { ref: fromAttr };
  const q = query();
  const fromUrl = q.get('formguid') ?? q.get('form') ?? q.get('id');
  return { ref: fromUrl?.trim() || null };
}

const FALSEY = /^(0|false|no|off)$/i;
const TRUTHY = /^(1|true|yes|on)$/i;

/**
 * `data-title`: absent or true means the form's own title, a falsey word means
 * no heading at all, anything else is the heading to show instead.
 */
function headingFrom(raw: string | undefined): boolean | string {
  if (raw === undefined) return true;
  if (FALSEY.test(raw)) return false;
  if (TRUTHY.test(raw)) return true;
  return raw;
}

/**
 * Every element on the page asking for a form. Two WordPress blocks can each
 * write `id="custom-form-root"` — /care-ministries has its own form and the
 * shared contact section — and `querySelectorAll` finds both where
 * `getElementById` would silently render only the first. A page that wants a
 * third can use `class="custom-form-root"`.
 */
function mountPoints(config: WidgetConfig): HTMLElement[] {
  const selector = config.containerId
    ? `[id="${config.containerId}"], .${config.containerId}`
    : `[id="${DEFAULT_CONTAINER_ID}"], [id="${LEGACY_CONTAINER_ID}"], .${DEFAULT_CONTAINER_ID}`;
  return Array.from(document.querySelectorAll<HTMLElement>(selector));
}

function createWidget(config: WidgetConfig, container: HTMLElement, index: number): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  return new ShadowWidget({
    container,
    containerIds: config.containerId
      ? [config.containerId]
      : [DEFAULT_CONTAINER_ID, LEGACY_CONTAINER_ID],
    overlayHostId: index === 0 ? 'custom-form-overlay' : `custom-form-overlay-${index}`,
    fontId: 'custom-form-widget-montserrat',
    scrollLockFlag: '__CUSTOM_FORM_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Custom Form Widget',
    render: (container) => {
      const { ref } = formFromPage(container, config);
      const titleAttr = attr(container.dataset.title) ?? attr(container.dataset.showTitle);
      const phoneAttr = attr(container.dataset.phone);
      return (
        <CustomForm
          apiBaseUrl={apiBaseUrl}
          formRef={ref}
          heading={headingFrom(titleAttr)}
          presets={{
            campus: attr(container.dataset.campus) ?? null,
            interest: attr(container.dataset.interest) ?? null,
            answers: attr(container.dataset.answers) ?? null,
          }}
          phoneRequired={!(phoneAttr && /^(optional|0|false|no|off)$/i.test(phoneAttr))}
          submitLabel={attr(container.dataset.submit) ?? null}
        />
      );
    },
  });
}

if (typeof window !== 'undefined') {
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.CUSTOM_FORM_WIDGET_CONFIG || {}),
  };
  window.CUSTOM_FORM_WIDGET_CONFIG = config;

  if (!window.CustomFormWidget) {
    let mounted = 0;
    const mount = (container: HTMLElement) => {
      const widget = createWidget(config, container, mounted++);
      widget.init();
      window.CustomFormWidget ??= widget;
      return widget;
    };
    window.CustomFormMount = mount;
    const boot = () => {
      const points = mountPoints(config);
      if (!points.length) {
        console.error(`Custom Form Widget: no container found (expected #${DEFAULT_CONTAINER_ID})`);
        return;
      }
      points.forEach(mount);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default ShadowWidget;
